import { type IMapGroup } from '../MapGroup.ts';
import { type IMapLayer } from '../MapLayer.ts';

/**
 * Chiavi dei nodi dell'albero gruppi/layer condiviso
 * ({@link MapLayersTreeInput}). La chiave è ancorata all'identità di
 * dominio del nodo — `name` per i gruppi, `code` per i layer — e prefissata per
 * distinguere i due spazi. Il separatore `:` è sicuro perché `name`/`code`
 * seguono il pattern `^[a-zA-Z][\w-]*$` (vedi `widgetCodeSchema` e
 * `MapGroupSchema`), quindi non può comparire nel valore.
 *
 * Le chiavi sono derivate deterministicamente dove servono (costruzione
 * dell'albero, selezione, mappatura sulla visibilità OpenLayers) e non vengono
 * mai persistite: ciò che si salva sono gli oggetti `layers`/`groups`.
 */
const GROUP_KEY_PREFIX = 'group:';
const LAYER_KEY_PREFIX = 'layer:';

/**
 * Chiave del nodo virtuale "senza gruppo". È un sentinel fisso: non rappresenta
 * un'entità di dominio e non compare mai nel parse-back verso `code`/`name`.
 */
export const UNGROUPED_NODE_KEY = '__ungrouped__';

/** Chiave del nodo di un gruppo, a partire dal suo `name`. */
export function getGroupNodeKey(name: string): string {
  return `${GROUP_KEY_PREFIX}${name}`;
}

/** Chiave del nodo di un layer, a partire dal suo `code`. */
export function getLayerNodeKey(code: string): string {
  return `${LAYER_KEY_PREFIX}${code}`;
}

/**
 * `code` del layer se la chiave appartiene a un nodo layer, altrimenti `null`
 * (chiavi di gruppo e nodo virtuale). Usata nel parse-back della selezione.
 */
export function layerCodeFromNodeKey(key: string): string | null {
  return key.startsWith(LAYER_KEY_PREFIX)
    ? key.slice(LAYER_KEY_PREFIX.length)
    : null;
}

/**
 * Chiavi di tutti i nodi selezionabili (gruppi + layer) per lo stato "tutto
 * spuntato". I gruppi sono inclusi esplicitamente: uno vuoto (senza layer)
 * altrimenti non verrebbe selezionato. Il nodo virtuale "senza gruppo" è
 * escluso: lo deriva Ant dalla cascata dei figli.
 */
export function getSelectAllKeys(
  groups: IMapGroup[],
  layers: IMapLayer[],
): string[] {
  return [
    ...groups.map(group => getGroupNodeKey(group.name)),
    ...layers.map(layer => getLayerNodeKey(layer.code)),
  ];
}
